import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import Menu from '@components/v2/ui/Menu/Menu';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, ButtonBase, Stack, alpha } from '@mui/material';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../translation/TranslationContext';

const CARD_BORDER_WIDTH = '1px';
const DIVIDER_OPACITY = 0.3;

interface AllowOption {
  labelKey: LangEnum;
  descriptionKey: LangEnum;
  testId: string;
  onClick: () => void;
}

interface ToolApprovalCardProps {
  prompt: string;
  isExpanded: boolean;
  onAllow: () => void;
  onAllowAlways: () => void;
  onAllowAllChat: () => void;
  onDecline: () => void;
  disabled?: boolean;
}

const ToolApprovalCard = ({
  prompt,
  isExpanded,
  onAllow,
  onAllowAlways,
  onAllowAllChat,
  onDecline,
  disabled = false,
}: ToolApprovalCardProps) => {
  const { translate } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const isMenuOpen = Boolean(anchorEl);
  const closeMenu = () => setAnchorEl(null);

  const allowOptions: AllowOption[] = [
    {
      labelKey: LangEnum.RACHEL_APPROVAL_ALLOW_ONCE,
      descriptionKey: LangEnum.RACHEL_APPROVAL_ALLOW_ONCE_DESCRIPTION,
      testId: 'tool-approval-allow-once',
      onClick: onAllow,
    },
    {
      labelKey: LangEnum.RACHEL_APPROVAL_ALLOW_ALWAYS,
      descriptionKey: LangEnum.RACHEL_APPROVAL_ALLOW_ALWAYS_DESCRIPTION,
      testId: 'tool-approval-allow-always',
      onClick: onAllowAlways,
    },
    {
      labelKey: LangEnum.RACHEL_APPROVAL_ALLOW_ALL_CHAT,
      descriptionKey: LangEnum.RACHEL_APPROVAL_ALLOW_ALL_CHAT_DESCRIPTION,
      testId: 'tool-approval-allow-all-chat',
      onClick: onAllowAllChat,
    },
  ];

  const actions = (
    <Stack direction="row" gap={1.25} alignItems="center" sx={{ flexShrink: 0 }}>
      <Button
        variant="contained"
        color="secondary"
        size="small"
        testId="tool-approval-decline"
        onClick={onDecline}
        disabled={disabled}
        sx={{ minHeight: 30, px: 1.5 }}
      >
        {translate(LangEnum.GLOBAL_DECLINE)}
      </Button>
      <Stack
        direction="row"
        alignItems="stretch"
        sx={(theme) => ({
          height: 30,
          borderRadius: theme.shape.borderRadiusLg,
          backgroundColor: theme.palette.text.primary,
          color: theme.palette.background.paper,
          overflow: 'hidden',
          opacity: disabled ? 0.5 : 1,
        })}
      >
        <ButtonBase
          test-id="tool-approval-allow-once-main"
          onClick={onAllow}
          disabled={disabled}
          sx={(theme) => ({
            ...theme.typography.micro1,
            px: 1.5,
            '&:hover': {
              backgroundColor: alpha(theme.palette.background.paper, 0.15),
            },
          })}
        >
          {translate(LangEnum.RACHEL_APPROVAL_ALLOW_ONCE)}
        </ButtonBase>
        <ButtonBase
          test-id="tool-approval-allow-menu-trigger"
          aria-label={translate(LangEnum.RACHEL_HEADER_OPTIONS)}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          disabled={disabled}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={(theme) => ({
            px: 0.75,
            borderLeft: `1px solid ${alpha(theme.palette.background.paper, DIVIDER_OPACITY)}`,
            '&:hover': {
              backgroundColor: alpha(theme.palette.background.paper, 0.15),
            },
          })}
        >
          <Icon icon={isMenuOpen ? ChevronUp : ChevronDown} size="xs" />
        </ButtonBase>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          list: {
            disablePadding: true,
          },
        }}
      >
        {allowOptions.map((option) => (
          <MenuItem
            key={option.testId}
            sx={{ px: 1.5, py: 0.75 }}
            testId={option.testId}
            onClick={() => {
              closeMenu();
              option.onClick();
            }}
          >
            <Stack gap={0.25}>
              <Typography variant="body2" color="text.primary">
                {translate(option.labelKey)}
              </Typography>
              <Typography variant="micro1" color="text.secondary">
                {translate(option.descriptionKey)}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );

  return (
    <Box
      test-id="tool-approval-card"
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        overflow: 'hidden',
        borderRadius: `${theme.shape.borderRadius2xl}px`,
        border: `${CARD_BORDER_WIDTH} solid ${theme.palette.custom.border.primary}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[4],
      })}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{ px: 1.5, py: 0.75, color: 'text.secondary' }}
      >
        <Typography variant="micro1" noWrap>
          {translate(LangEnum.RACHEL_APPROVAL_TITLE)}
        </Typography>
      </Stack>

      <Stack gap={0.25} sx={{ px: 1.5, py: 0.5 }}>
        <Typography variant={isExpanded ? 'body1' : 'body2'} color="text.primary">
          {prompt}
        </Typography>
        <Typography variant="micro1" color="text.secondary">
          {translate(LangEnum.GLOBAL_UPDATE_IS_IRREVERSIBLE)}
        </Typography>
      </Stack>
      <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1.5, pr: 1, pb: 1, pl: 0.75 }}>
        {actions}
      </Stack>
    </Box>
  );
};

export default ToolApprovalCard;
